import { logger } from "@climatepartner/app-shell/monitoring";
import { toast } from "@climatepartner/components/Toast";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { RestError } from "../lib/exceptions";

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: {
      /** Opt a query into the global error toast, with the message to show. */
      errorMessage?: string;
    };
    mutationMeta: {
      /** Opt out of the global error toast, for mutations that render their failure themselves or already toast. */
      silentError?: boolean;
    };
  }
}

export const genericErrorMessage = "Something went wrong. Please try again.";

/**
 * Only a `RestError` carries a user-facing message; any other failure's message names our own
 * internals. The `typeof` guard is not redundant: `message` is copied verbatim off the wire, and a
 * class-validator `BadRequestException` puts objects there, which makes React throw "Objects are
 * not valid as a React child" inside the toast.
 */
export const toErrorMessage = (error: unknown): string =>
  isClientErrorWithMessage(error) ? error.message : genericErrorMessage;

/** A 4xx `RestError` with a string message; a 5xx means a bug the user cannot act on. */
export const isClientErrorWithMessage = (error: unknown): error is RestError =>
  error instanceof RestError && error.statusCode < 500 && typeof error.message === "string" && error.message !== "";

const onMutationError = (error: unknown, meta: { silentError?: boolean } | undefined): void => {
  if (meta?.silentError) {
    return;
  }
  toast.error(toErrorMessage(error));
};

/**
 * Reads toast only when they ask to: a failed background refetch must not interrupt a page still
 * showing good data, and a read that already renders an error page would toast on top of it.
 */
const onQueryError = (error: unknown, meta: { errorMessage?: string } | undefined): void => {
  if (!meta?.errorMessage) {
    return;
  }
  toast.error(meta.errorMessage);
  logger.error(toErrorMessage(error), {
    type: "query-error",
    statusCode: error instanceof RestError ? error.statusCode : undefined,
  });
};

export const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 30000,
      },
    },
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => onMutationError(error, mutation.options.meta),
    }),
    queryCache: new QueryCache({
      onError: (error, query) => onQueryError(error, query.meta),
    }),
  });
